import React, { Suspense, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import Login from "./login";
import Signup from "./signup";
import Home from "./component/Home";
import VerifyEmail from "./VerifyEmail";
import { store } from "./store";
import { Provider, useSelector } from "react-redux";
import Layout from "./component/layout";
import Room from "./component/Room";
import ResetPassword from "./resetPassword";
import ResetPasswordConfirm from "./ResetPasswordConfirm";
import { ToastContainer, cssTransition } from 'react-toastify';
import "./Toast.css";
const AdminDashboard = React.lazy(() => import("./admin-dashboard"));
const TenantAdminDashboard = React.lazy(() => import("./component/TenantAdminDashboard"));
const TenantAdminOverview = React.lazy(() => import("./component/tenant-admin/TenantAdminOverview"));
const TenantAdminUsers = React.lazy(() => import("./component/tenant-admin/TenantAdminUsers"));
const TenantAdminDocuments = React.lazy(() => import("./component/tenant-admin/TenantAdminDocuments"));
import AdminLogin from "./admin-login";
import TenantAdminRoute from "./component/TenantAdminRoute";
import Terms from "./terms";
import Privacy from "./privacy_policy";
import ErrorBoundary from "./component/ErrorBoundary";
import LandingPage from "./LandingPage";

// FIX 11: Verify token existence AND that it hasn't expired
const isTokenValid = (tokenKey = "access_token") => {
  const token = localStorage.getItem(tokenKey);
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    if (payload.exp * 1000 <= Date.now()) {
      // Token expired — clear stale credentials
      localStorage.removeItem(tokenKey);
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

function PrivateRoute({ children }) {
  if (!isTokenValid("access_token")) {
    return <Navigate to="/login" />;
  }
  return <Layout>{children}</Layout>;
}

/** Remount Room on roomId change so local state cannot leak between chats. */
function RoomWrapper() {
  const { roomId } = useParams();
  return <Room key={roomId} />;
}

// FIX 8: Admin-only route — requires a valid admin_access_token.
// The backend admin-login endpoint already enforces is_staff before issuing the
// token, so a valid admin_access_token is sufficient proof of admin status.
// SimpleJWT does not embed is_staff/is_superuser in the payload by default,
// so we do NOT try to read those claims here (they'd always be undefined → false).
function AdminRoute({ children }) {
  if (!isTokenValid("admin_access_token")) {
    return <Navigate to="/admin-login" replace />;
  }
  return children;
}

// Theme wrapper to manage global theme application
const ThemeWrapper = ({ children }) => {
  const theme = useSelector((state) => state.theme.theme); // Get theme from Redux

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme || "dark"); // Default to dark
  }, [theme]);

  return children;
};

// Toast entrance/exit — keyframes live in Toast.css and match the app's shared
// one-shot entrance (cwWelcomeEnter). Replaces the library's 0.5s bounce.
const toastTransition = cssTransition({
  enter: "se-toast-enter",
  exit: "se-toast-exit",
  collapseDuration: 180,
});

// react-toastify picks a theme class per toast from its own `theme` prop, so it
// needs the value passed in — the data-theme attribute alone does not reach it.
const ThemedToastContainer = () => {
  const theme = useSelector((state) => state.theme.theme);
  return (
    <ToastContainer
      position="top-right"
      theme={theme === "light" ? "light" : "dark"}
      transition={toastTransition}
      autoClose={4000}
      limit={3}
      newestOnTop
    />
  );
};

function RootRoute() {
  if (isTokenValid("access_token")) {
    return <Navigate to="/chat" replace />;
  }
  return <LandingPage />;
}

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <Router>
          <ThemeWrapper>
            <div className="app-container d-flex">
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/" element={<RootRoute />} />
                <Route path="/chat" element={<PrivateRoute><Home /></PrivateRoute>} />
                <Route
                  path="/tenant-admin"
                  element={
                    <TenantAdminRoute>
                      <Suspense fallback={<div className="ta-route-loading">Loading…</div>}>
                        <TenantAdminDashboard />
                      </Suspense>
                    </TenantAdminRoute>
                  }
                >
                  <Route index element={<TenantAdminOverview />} />
                  <Route path="users" element={<TenantAdminUsers />} />
                  <Route path="documents" element={<TenantAdminDocuments />} />
                  <Route path="*" element={<Navigate to="/tenant-admin" replace />} />
                </Route>
                <Route path="/:roomId" element={<PrivateRoute><RoomWrapper /></PrivateRoute>} />
                <Route path="/register" element={<Signup />} />
                <Route path="/verify-email/:token" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ResetPassword />} />
                <Route path="/reset-password/:uid/:token" element={<ResetPasswordConfirm />} />
                <Route
                  path="/admin-dashboard"
                  element={
                    <AdminRoute>
                      <Suspense fallback={<div className="p-4">Loading admin dashboard…</div>}>
                        <AdminDashboard />
                      </Suspense>
                    </AdminRoute>
                  }
                />
                <Route path="/admin-login" element={<AdminLogin />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
              </Routes>
              <ThemedToastContainer />
            </div>
          </ThemeWrapper>
        </Router>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
